import { baseApi } from "./baseApi";
import type { Incident } from "../api";

interface IncidentListParams {
  state?: string;
  severity?: string;
  limit?: number;
}

export const incidentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getIncidents: builder.query<Incident[], IncidentListParams | void>({
      query: (params) => {
        const qs = new URLSearchParams();
        if (params?.state) qs.set("state", params.state);
        if (params?.severity) qs.set("severity", params.severity);
        if (params?.limit) qs.set("limit", String(params.limit));
        const tail = qs.toString();
        return tail ? `/v1/incidents?${tail}` : "/v1/incidents";
      },
      transformResponse: (r: { incidents: Incident[] }) => r.incidents,
      providesTags: (result) =>
        result
          ? [
              ...result.map((i) => ({ type: "Stats" as const, id: `incident-${i.id}` })),
              { type: "Stats", id: "incidents-LIST" },
            ]
          : [{ type: "Stats", id: "incidents-LIST" }],
    }),

    getIncident: builder.query<Incident, string>({
      query: (id) => `/v1/incidents/${id}`,
      providesTags: (_r, _e, id) => [{ type: "Stats", id: `incident-${id}` }],
    }),

    createIncident: builder.mutation<Incident, Partial<Incident> & { title: string }>({
      query: (body) => ({ url: "/v1/incidents", method: "POST", body }),
      invalidatesTags: [{ type: "Stats", id: "incidents-LIST" }, { type: "Stats", id: "sidebar" }],
    }),

    updateIncident: builder.mutation<Incident, { id: string } & Partial<Incident>>({
      query: ({ id, ...body }) => ({ url: `/v1/incidents/${id}`, method: "PATCH", body }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "Stats", id: `incident-${arg.id}` },
        { type: "Stats", id: "incidents-LIST" },
      ],
    }),

    addIncidentUpdate: builder.mutation<void, { id: string; message: string; new_state?: string }>({
      query: ({ id, ...body }) => ({
        url: `/v1/incidents/${id}/updates`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: "Stats", id: `incident-${arg.id}` }],
    }),

    resolveIncident: builder.mutation<void, { id: string; message?: string }>({
      query: ({ id, ...body }) => ({
        url: `/v1/incidents/${id}/resolve`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "Stats", id: `incident-${arg.id}` },
        { type: "Stats", id: "incidents-LIST" },
        { type: "Stats", id: "sidebar" },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetIncidentsQuery,
  useGetIncidentQuery,
  useCreateIncidentMutation,
  useUpdateIncidentMutation,
  useAddIncidentUpdateMutation,
  useResolveIncidentMutation,
} = incidentsApi;
